import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Shield, 
  CheckCircle2,
  ChevronLeft,
  Star,
  Menu,
  X,
  Facebook,
  Wrench,
  Sparkles,
  Calendar,
  Search,
  ThumbsUp
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./components/ui/select";
import { Toaster, toast } from "sonner";
import { SEO } from "./components/SEO";
import { FAQ } from "./components/FAQ";
import { FAQS } from "./data/faqs";
import { SERVICE_PAGES } from "./data/services";
import { ALL_AREAS } from "./data/areas";
import { ServicePage } from "./pages/ServicePage";
import { AreaPage } from "./pages/AreaPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phone URL for conversion tracking
const PHONE_URL = 'tel:+16094512225';

// Phone click handler - fires Google Ads conversion then redirects to phone
const handlePhoneClick = (e) => {
  e.preventDefault();
  
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17526247982/6ELDCNWcmu0bEK6klaVB',
      'value': 1.0,
      'currency': 'USD',
      'event_callback': function() {
        window.location.href = PHONE_URL;
      }
    });
    
    setTimeout(function() {
      window.location.href = PHONE_URL;
    }, 1000);
  } else {
    window.location.href = PHONE_URL;
  }
};

// Business Info
const BUSINESS = {
  name: "Major Appliance Repair",
  phone: "+1 609 451 2225",
  phoneFormatted: "(609) 451-2225",
  email: "Hello@bbmappliances.com",
  areas: ["Gloucester County", "North Burlington County", "Camden County"],
  address: "1099 White Horse Rd 2nd Floor, Voorhees Township, NJ 08043",
  googleMapsUrl: "https://www.google.com/maps/place/Major+Appliance+Repair+NJ/@39.8436,-74.9725,15z",
  facebook: "https://www.facebook.com/profile.php?id=61583085047292",
  googleRating: 4.9,
  googleReviewCount: 159
};

// Services Data
const SERVICES = [
  { 
    id: "refrigerator",
    name: "Refrigerator", 
    zenbookerUrl: "https://widget.zenbooker.com/book/1697122450472x556377720512791740?embed=true&preselected-service=1697131454753x470138417414668300"
  },
  { 
    id: "oven",
    name: "Oven and Stove", 
    zenbookerUrl: "https://widget.zenbooker.com/book/1697122450472x556377720512791740?embed=true&preselected-service=1697132211443x112686808887197700"
  },
  { 
    id: "dryer",
    name: "Dryer", 
    zenbookerUrl: "https://widget.zenbooker.com/book/1697122450472x556377720512791740?embed=true&preselected-service=1697130679646x469888136453292000"
  },
  { 
    id: "washer",
    name: "Washer", 
    zenbookerUrl: "https://widget.zenbooker.com/book/1697122450472x556377720512791740?embed=true&preselected-service=1697122887634x335859285365620740"
  },
  { 
    id: "dishwasher",
    name: "Dishwasher", 
    zenbookerUrl: "https://widget.zenbooker.com/book/1697122450472x556377720512791740?embed=true&preselected-service=1724414703177x278134466012439200"
  },
  { 
    id: "garbage-disposal",
    name: "Garbage Disposal", 
    zenbookerUrl: "https://widget.zenbooker.com/book/1697122450472x556377720512791740?embed=true&preselected-service=1700878787321x804045750988087600"
  }
];

// Brands
const BRANDS = ["GE", "LG", "Maytag", "Samsung", "Whirlpool", "Frigidaire", "KitchenAid", "Bosch"];

// Header Component - BenTech-style warm & bold
const Header = ({ minimal = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="bg-cream/80 backdrop-blur-sm border-b border-orange-100 sticky top-0 z-30" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo with wrench icon */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-11 h-11 bg-orange-500 rounded-full flex items-center justify-center shadow-sm">
              <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-lg text-navy">Major Appliance</span>
              <span className="text-[10px] tracking-[0.15em] uppercase text-orange-500 font-semibold">Repair NJ</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          {!minimal && (
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-navy hover:text-orange-500 text-sm font-semibold transition-colors">Services</Link>
              <Link to="/#how" className="text-navy hover:text-orange-500 text-sm font-semibold transition-colors">How it Works</Link>
              <Link to="/#reviews" className="text-navy hover:text-orange-500 text-sm font-semibold transition-colors">Reviews</Link>
              <Link to="/#faq" className="text-navy hover:text-orange-500 text-sm font-semibold transition-colors">FAQ</Link>
              <Link to="/#contact" className="text-navy hover:text-orange-500 text-sm font-semibold transition-colors">Contact</Link>
            </nav>
          )}

          {/* Book Online CTA - orange pill (primary) */}
          <a 
            href="/#services"
            className="hidden md:inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 rounded-full font-semibold text-sm shadow-md shadow-orange-500/20 transition-all"
            data-testid="header-book"
          >
            Book Online
            <span className="bg-white/25 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Save 5%</span>
          </a>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-navy"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-orange-100 py-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              <Link to="/#services" className="px-4 py-3 text-navy font-medium hover:bg-orange-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/#how" className="px-4 py-3 text-navy font-medium hover:bg-orange-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>How it Works</Link>
              <Link to="/#reviews" className="px-4 py-3 text-navy font-medium hover:bg-orange-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Reviews</Link>
              <Link to="/#faq" className="px-4 py-3 text-navy font-medium hover:bg-orange-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>FAQ</Link>
              <Link to="/#contact" className="px-4 py-3 text-navy font-medium hover:bg-orange-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <a 
                href={PHONE_URL}
                onClick={handlePhoneClick}
                className="mx-4 mt-3 bg-orange-500 text-white text-center py-3 rounded-full font-semibold flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" /> Call {BUSINESS.phoneFormatted}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero - BenTech-style warm & chunky
const Hero = () => {
  return (
    <section className="hero-gradient relative overflow-hidden" data-testid="hero-section">
      {/* Decorative circles */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-40 left-1/2 w-80 h-80 bg-orange-100/50 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-orange-200 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-600 uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Save 5% When You Book Online
            </span>

            <h1 className="font-display text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">
              South Jersey's<br />
              <span className="text-orange-500">Top Appliance</span><br />
              Repair Team.
            </h1>

            <p className="text-lg text-navy/70 max-w-lg mb-8">
              Refrigerator not cooling? Washer leaking? Oven acting up? Our certified technicians fix it right — the first time. Serving Gloucester, North Burlington and Camden Counties.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <a href="#services" className="w-full sm:w-auto">
                <Button 
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-7 py-6 text-base font-semibold w-full shadow-lg shadow-orange-500/25"
                  data-testid="hero-book-btn"
                >
                  Book Online & Save 5%
                </Button>
              </a>
              <a 
                href={PHONE_URL}
                onClick={handlePhoneClick}
                className="inline-flex items-center justify-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white px-7 py-4 rounded-full font-semibold text-base transition-all"
                data-testid="hero-phone"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} /> Or call {BUSINESS.phoneFormatted}
              </a>
            </div>

            {/* Trust indicators row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2 text-navy">
                <Clock className="w-5 h-5 text-orange-500 flex-shrink-0" strokeWidth={2.5} />
                <span className="text-xs font-semibold">Same-Day Service</span>
              </div>
              <div className="flex items-center gap-2 text-navy">
                <Shield className="w-5 h-5 text-orange-500 flex-shrink-0" strokeWidth={2.5} />
                <span className="text-xs font-semibold">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2 text-navy">
                <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" strokeWidth={2.5} />
                <span className="text-xs font-semibold">30-Day Warranty</span>
              </div>
            </div>
          </div>

          {/* Right - Technician image */}
          <div className="hidden md:block relative">
            <div className="absolute inset-0 bg-orange-200/30 rounded-[3rem] rotate-3" />
            <img 
              src="https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=700&q=80"
              alt="Appliance Repair Technician"
              className="relative w-full rounded-[3rem] shadow-2xl object-cover aspect-[4/5]"
            />
            {/* Floating stat card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-orange-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-orange-500 fill-orange-500" />
                </div>
                <div>
                  <div className="text-2xl font-display text-navy leading-none">{BUSINESS.googleRating}</div>
                  <div className="text-xs text-navy/60">{BUSINESS.googleReviewCount} Google Reviews</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Fee Banner - warm orange strip with online discount
const ServiceFeeBanner = () => {
  return (
    <div className="bg-navy py-4">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <p className="text-white font-medium text-sm md:text-base">
          <span className="inline-block bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full mr-2 align-middle">SAVE 5%</span>
          Book online for a 5% discount.
          <span className="text-white/60"> · </span>
          <span className="text-orange-400 font-bold">$99</span> service call fee
          <span className="text-white/60"> ($109 for refrigerators) — waived when you proceed with the repair.</span>
        </p>
      </div>
    </div>
  );
};

// Appliance Icons (SVG)
const ApplianceIcons = {
  refrigerator: () => (
    <svg viewBox="0 0 64 64" className="w-16 h-16" fill="none" stroke="#4B5563" strokeWidth="1.5">
      <rect x="16" y="4" width="32" height="56" rx="2" fill="#F3F4F6"/>
      <line x1="16" y1="20" x2="48" y2="20"/>
      <circle cx="42" cy="12" r="2" fill="#4B5563"/>
      <circle cx="42" cy="30" r="2" fill="#4B5563"/>
      <rect x="20" y="24" width="24" height="8" rx="1" fill="#E5E7EB"/>
    </svg>
  ),
  oven: () => (
    <svg viewBox="0 0 64 64" className="w-16 h-16" fill="none" stroke="#4B5563" strokeWidth="1.5">
      <rect x="8" y="8" width="48" height="48" rx="2" fill="#F3F4F6"/>
      <rect x="14" y="24" width="36" height="28" rx="1" fill="#E5E7EB"/>
      <circle cx="18" cy="14" r="3" fill="#6B7280"/>
      <circle cx="28" cy="14" r="3" fill="#6B7280"/>
      <circle cx="38" cy="14" r="3" fill="#6B7280"/>
      <circle cx="48" cy="14" r="3" fill="#6B7280"/>
    </svg>
  ),
  dryer: () => (
    <svg viewBox="0 0 64 64" className="w-16 h-16" fill="none" stroke="#4B5563" strokeWidth="1.5">
      <rect x="12" y="8" width="40" height="48" rx="2" fill="#F3F4F6"/>
      <circle cx="32" cy="36" r="14" fill="#E5E7EB"/>
      <circle cx="32" cy="36" r="8" fill="#D1D5DB"/>
      <rect x="16" y="12" width="8" height="4" rx="1" fill="#6B7280"/>
      <rect x="40" y="12" width="8" height="4" rx="1" fill="#6B7280"/>
    </svg>
  ),
  washer: () => (
    <svg viewBox="0 0 64 64" className="w-16 h-16" fill="none" stroke="#4B5563" strokeWidth="1.5">
      <rect x="12" y="8" width="40" height="48" rx="2" fill="#F3F4F6"/>
      <circle cx="32" cy="36" r="14" fill="#E5E7EB"/>
      <circle cx="32" cy="36" r="10" fill="#3B82F6" opacity="0.3"/>
      <rect x="16" y="12" width="6" height="4" rx="1" fill="#3B82F6"/>
      <rect x="24" y="12" width="6" height="4" rx="1" fill="#6B7280"/>
    </svg>
  ),
  dishwasher: () => (
    <svg viewBox="0 0 64 64" className="w-16 h-16" fill="none" stroke="#4B5563" strokeWidth="1.5">
      <rect x="12" y="8" width="40" height="48" rx="2" fill="#F3F4F6"/>
      <rect x="16" y="20" width="32" height="32" rx="1" fill="#E5E7EB"/>
      <line x1="20" y1="28" x2="44" y2="28"/>
      <line x1="20" y1="36" x2="44" y2="36"/>
      <circle cx="24" cy="44" r="3" fill="#D1D5DB"/>
      <circle cx="32" cy="44" r="3" fill="#D1D5DB"/>
      <circle cx="40" cy="44" r="3" fill="#D1D5DB"/>
      <rect x="28" y="10" width="8" height="4" rx="1" fill="#6B7280"/>
    </svg>
  ),
  "garbage-disposal": () => (
    <svg viewBox="0 0 64 64" className="w-16 h-16" fill="none" stroke="#4B5563" strokeWidth="1.5">
      <rect x="20" y="8" width="24" height="8" rx="1" fill="#6B7280"/>
      <path d="M18 16 L46 16 L44 52 L20 52 Z" fill="#F3F4F6"/>
      <line x1="24" y1="24" x2="24" y2="44"/>
      <line x1="32" y1="24" x2="32" y2="44"/>
      <line x1="40" y1="24" x2="40" y2="44"/>
      <rect x="28" y="52" width="8" height="8" fill="#6B7280"/>
    </svg>
  )
};

// Services Section - BenTech-style image cards
const SERVICE_IMAGES = {
  refrigerator: "https://images.unsplash.com/photo-1721613877687-c9099b698faa?w=600&q=80",
  oven: "https://images.unsplash.com/photo-1556909114-4f5f4a35d18d?w=600&q=80",
  dryer: "https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?w=600&q=80",
  washer: "https://images.unsplash.com/photo-1626806819282-2c1dc01a5e0c?w=600&q=80",
  dishwasher: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=600&q=80",
  "garbage-disposal": "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80",
};

const SERVICE_BLURBS = {
  refrigerator: {
    tag: "Warm fridge? Frost buildup? Weird noises?",
    bullets: ["Compressor & thermostat", "Ice makers & seals", "Water dispenser leaks"],
  },
  washer: {
    tag: "Won't spin, drain or start.",
    bullets: ["Drum & pump", "Leaking hoses", "Balance & bearings"],
  },
  dryer: {
    tag: "No heat, no tumble, no problem.",
    bullets: ["Heating element", "Belt & vent cleaning", "Thermal fuse & sensors"],
  },
  oven: {
    tag: "Uneven baking or bad ignition.",
    bullets: ["Gas & electric", "Ignitors, elements, sensors", "Broiler & convection"],
  },
  dishwasher: {
    tag: "Dirty dishes? Standing water?",
    bullets: ["Pumps & spray arms", "Door seals & drains", "Detergent dispensers"],
  },
  "garbage-disposal": {
    tag: "Jammed, humming, or leaking?",
    bullets: ["Motor & flywheel", "Leaks & clogs", "Full replacements"],
  },
};

const Services = () => {
  return (
    <section id="services" className="bg-white py-20" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <span className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3 inline-block">What we fix</span>
          <h2 className="font-display text-3xl md:text-5xl text-navy leading-tight mb-4">
            Every major appliance,<br />
            <span className="text-orange-500">one trusted team.</span>
          </h2>
          <p className="text-navy/60 max-w-2xl mx-auto text-lg">
            We service all makes and models — GE, Whirlpool, Samsung, LG, Bosch, KitchenAid, Maytag, and more.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service) => {
            const blurb = SERVICE_BLURBS[service.id];
            const img = SERVICE_IMAGES[service.id];
            return (
              <Link
                key={service.id}
                to={`/book/${service.id}`}
                className="group bg-cream rounded-3xl overflow-hidden border border-orange-100 hover:border-orange-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`service-card-${service.id}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-orange-100">
                  <img
                    src={img}
                    alt={`${service.name} Repair`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-bold text-orange-500 uppercase tracking-widest">Repair</span>
                  <h3 className="font-display text-2xl text-navy mt-1 mb-2">{service.name}</h3>
                  <p className="text-navy/60 text-sm mb-4">{blurb.tag}</p>
                  <ul className="space-y-1.5 mb-5">
                    {blurb.bullets.map((b, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-navy/80">
                        <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0" strokeWidth={2.5} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <span className="inline-flex items-center gap-1 text-orange-500 group-hover:text-orange-600 font-semibold text-sm">
                    Book this repair <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// How It Works - 4-step process (BenTech style)
const HowItWorks = () => {
  const steps = [
    { icon: Calendar, num: "1", title: "Book", desc: "Call us or submit the form — pick a time that works for you." },
    { icon: Search, num: "2", title: "Diagnose", desc: "A certified technician arrives, inspects, and gives you an upfront quote." },
    { icon: Wrench, num: "3", title: "Repair", desc: "We fix it on the spot when possible — with quality parts and care." },
    { icon: ThumbsUp, num: "4", title: "Enjoy", desc: "Back to laundry, cooking, and cold snacks — backed by our warranty." },
  ];

  return (
    <section id="how" className="bg-cream py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <span className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3 inline-block">The Process</span>
          <h2 className="font-display text-3xl md:text-5xl text-navy leading-tight mb-4">
            Simple, honest,<br />
            <span className="text-orange-500">and fast.</span>
          </h2>
          <p className="text-navy/60 max-w-2xl mx-auto text-lg">
            No mystery fees. No pushy upsells. Just a friendly technician who respects your time and your home.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-3xl p-8 border border-orange-100 hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-display text-xl shadow-lg">
                {s.num}
              </div>
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-4">
                <s.icon className="w-7 h-7 text-orange-500" strokeWidth={2} />
              </div>
              <h3 className="font-display text-xl text-navy mb-2">{s.title}</h3>
              <p className="text-navy/60 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Brands Section - warm variant
const BrandsSection = () => {
  return (
    <section className="bg-white py-14">
      <div className="max-w-6xl mx-auto px-4">
        <p className="text-center text-xs font-bold text-orange-500 uppercase tracking-widest mb-8">We Service All Major Brands</p>
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-14">
          {BRANDS.map((brand) => (
            <span key={brand} className="font-display text-xl text-navy/30 hover:text-navy transition-colors">
              {brand}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Us - BenTech style with technician photo
const WhyUs = () => {
  const features = [
    { icon: Shield, title: "Licensed & Insured", desc: "Every technician is background-checked and fully insured for your peace of mind." },
    { icon: Clock, title: "Same-Day Service", desc: "Book before noon and we'll be at your door today, most days of the week." },
    { icon: CheckCircle2, title: "Upfront Pricing", desc: "$99 service call fee ($109 refrigerators) — waived when you proceed with the repair." },
    { icon: Sparkles, title: "30-Day Warranty", desc: "All parts and labor are covered — if it breaks again, we come back free." },
    { icon: ThumbsUp, title: "Local & Trusted", desc: "Based right here in Voorhees Township, NJ. Serving South Jersey since day one." },
    { icon: Wrench, title: "All Major Brands", desc: "GE, LG, Samsung, Whirlpool, Maytag, KitchenAid, Bosch, Frigidaire and more." },
  ];

  return (
    <section className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-14">
          <div>
            <span className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3 inline-block">Why Us</span>
            <h2 className="font-display text-3xl md:text-5xl text-navy leading-tight mb-4">
              Neighbors you can call.<br />
              <span className="text-orange-500">Repairs you can trust.</span>
            </h2>
            <p className="text-navy/70 text-lg">
              We treat every home like it's our own. That's what being a local South Jersey business really means — 4.9 stars across {BUSINESS.googleReviewCount} Google reviews and counting.
            </p>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-orange-200/40 rounded-[3rem] -rotate-3" />
            <img 
              src="https://images.unsplash.com/photo-1732395805034-e0bf859665e5?w=700&q=80" 
              alt="Certified appliance repair technician"
              className="relative w-full rounded-[3rem] shadow-xl object-cover aspect-[4/3]"
            />
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 border border-orange-100 max-w-[180px]">
              <p className="font-display text-navy text-sm">Certified Technicians</p>
              <p className="text-xs text-navy/60 mt-1">Licensed & insured in NJ</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => (
            <div key={i} className="bg-cream rounded-2xl p-6 border border-orange-100">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <h3 className="font-display text-lg text-navy mb-2">{feature.title}</h3>
              <p className="text-navy/60 text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews - BenTech style
const Reviews = () => {
  const reviews = [
    { name: "Derek L.", initials: "DL", location: "Homeowner · Voorhees", text: "High-quality on-time service with a focus on customer education! The tech walked me through every step and had my fridge running the same day." },
    { name: "John L.", initials: "JL", location: "Landlord · Cherry Hill", text: "Diagnosed the problem quickly and fixed it the same day. Fair pricing and no surprise charges. I manage 8 units and they're my only call now." },
    { name: "Maria M.", initials: "MM", location: "Homeowner · Mount Laurel", text: "Our washer works perfectly now. Great service, showed up on time, and cleaned up after the repair. Highly recommend." },
    { name: "Kevin S.", initials: "KS", location: "Homeowner · Marlton", text: "Called them Monday morning, they were here Tuesday. Fixed my dryer's heating element in under an hour. Professional and honest." },
  ];

  return (
    <section id="reviews" className="bg-cream py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <span className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3 inline-block">Real Reviews</span>
          <h2 className="font-display text-3xl md:text-5xl text-navy leading-tight mb-4">
            Loved by folks in<br />
            <span className="text-orange-500">the neighborhood.</span>
          </h2>
          <div className="inline-flex items-center gap-3 bg-white rounded-full px-5 py-2 border border-orange-100 mt-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-orange-500 fill-orange-500" />
              ))}
            </div>
            <span className="font-display text-navy">{BUSINESS.googleRating}/5</span>
            <span className="text-navy/60 text-sm">from {BUSINESS.googleReviewCount}+ reviews</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {reviews.map((r, i) => (
            <div key={i} className="bg-white rounded-3xl p-7 border border-orange-100 hover:shadow-lg transition-shadow">
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-orange-500 fill-orange-500" />
                ))}
              </div>
              <p className="text-navy/80 mb-6 leading-relaxed">"{r.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-display text-sm">
                  {r.initials}
                </div>
                <div>
                  <p className="font-display text-navy text-sm">{r.name}</p>
                  <p className="text-xs text-navy/60">{r.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Areas - warm pills
const ServiceAreas = () => {
  return (
    <section className="bg-white py-20 border-t border-orange-100" data-testid="service-areas-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3 inline-block">Service Areas</span>
          <h2 className="font-display text-3xl md:text-5xl text-navy leading-tight mb-4">
            Serving South Jersey<br />
            <span className="text-orange-500">across three counties.</span>
          </h2>
          <p className="text-navy/60 text-lg">
            Trusted appliance repair across Gloucester, Camden, and North Burlington Counties.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {ALL_AREAS.map((a) => (
            <Link
              key={a.slug}
              to={`/appliance-repair-${a.slug}-nj`}
              className="bg-cream hover:bg-orange-500 border border-orange-100 hover:border-orange-500 text-navy hover:text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all"
              data-testid={`area-link-${a.slug}`}
            >
              {a.name}, NJ
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section - BenTech card style
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Request submitted! We'll contact you soon.");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please call us.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-cream py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <span className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3 inline-block">Get in touch</span>
            <h2 className="font-display text-3xl md:text-5xl text-navy leading-tight mb-4">
              Have a question first?<br />
              <span className="text-orange-500">Just say hi.</span>
            </h2>
            <p className="text-navy/70 text-lg mb-8">
              The fastest way to reach us is by phone — we usually pick up in under a minute. Or drop us a note and we'll get back within business hours.
            </p>

            <div className="space-y-4">
              <a href={PHONE_URL} onClick={handlePhoneClick} className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-orange-100 hover:border-orange-300 transition-colors">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-xs text-navy/60 font-semibold uppercase tracking-wider">Call us</p>
                  <p className="font-display text-navy text-lg">{BUSINESS.phoneFormatted}</p>
                </div>
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-orange-100 hover:border-orange-300 transition-colors">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-xs text-navy/60 font-semibold uppercase tracking-wider">Email</p>
                  <p className="font-display text-navy text-sm">{BUSINESS.email}</p>
                </div>
              </a>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-5 border border-orange-100">
                  <p className="text-xs text-navy/60 font-semibold uppercase tracking-wider mb-1">Hours</p>
                  <p className="text-navy text-sm font-medium">Mon–Sat<br />8:00 AM – 8:00 PM</p>
                </div>
                <div className="bg-white rounded-2xl p-5 border border-orange-100">
                  <p className="text-xs text-navy/60 font-semibold uppercase tracking-wider mb-1">Area</p>
                  <p className="text-navy text-sm font-medium">Gloucester, Camden & N. Burlington Counties</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 border border-orange-100 shadow-lg">
            <div className="mb-6">
              <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Send a message</span>
              <h3 className="font-display text-2xl text-navy">We'll reply within one business day.</h3>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="text"
                required
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="h-12 border-orange-100 focus-visible:ring-orange-500 rounded-xl bg-cream"
                data-testid="contact-name-input"
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="email"
                  required
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="h-12 border-orange-100 focus-visible:ring-orange-500 rounded-xl bg-cream"
                  data-testid="contact-email-input"
                />
                <Input
                  type="tel"
                  required
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="h-12 border-orange-100 focus-visible:ring-orange-500 rounded-xl bg-cream"
                  data-testid="contact-phone-input"
                />
              </div>
              <Select
                value={formData.service}
                onValueChange={(value) => setFormData({...formData, service: value})}
              >
                <SelectTrigger className="h-12 border-orange-100 focus:ring-orange-500 rounded-xl bg-cream" data-testid="contact-service-select">
                  <SelectValue placeholder="Select appliance..." />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Describe the issue..."
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="border-orange-100 focus-visible:ring-orange-500 rounded-xl bg-cream min-h-[120px]"
                data-testid="contact-message-input"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-13 py-6 font-semibold rounded-full shadow-lg shadow-orange-500/25"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer - navy warm
const Footer = () => {
  return (
    <footer className="bg-navy text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-orange-500 rounded-full flex items-center justify-center">
                <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-display text-lg text-white">Major Appliance</span>
                <span className="text-[10px] tracking-[0.15em] uppercase text-orange-400 font-semibold">Repair NJ</span>
              </div>
            </div>
            <p className="text-white/60 text-sm mb-5">Your trusted appliance repair service in South Jersey since day one.</p>
            <div className="space-y-2.5 text-sm">
              <a href={PHONE_URL} onClick={handlePhoneClick} className="flex items-center gap-2 text-white/70 hover:text-orange-400" data-testid="footer-phone">
                <Phone className="w-4 h-4" /> {BUSINESS.phoneFormatted}
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2 text-white/70 hover:text-orange-400" data-testid="footer-email">
                <Mail className="w-4 h-4" /> {BUSINESS.email}
              </a>
              <div className="flex items-start gap-2 text-white/70">
                <MapPin className="w-4 h-4 mt-0.5" /> 
                <span>{BUSINESS.address}</span>
              </div>
              <a href={BUSINESS.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/70 hover:text-orange-400">
                <Facebook className="w-4 h-4" /> Facebook
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-display text-white mb-4">Repair Services</h4>
            <ul className="space-y-2 text-sm">
              {SERVICE_PAGES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="text-white/60 hover:text-orange-400 transition-colors" data-testid={`footer-service-${s.id}`}>
                    {s.name} Repair
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white mb-4">Service Areas</h4>
            <ul className="space-y-2 text-sm">
              {ALL_AREAS.slice(0, 8).map((a) => (
                <li key={a.slug}>
                  <Link to={`/appliance-repair-${a.slug}-nj`} className="text-white/60 hover:text-orange-400 transition-colors">
                    {a.name}, NJ
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white mb-4">More Areas</h4>
            <ul className="space-y-2 text-sm">
              {ALL_AREAS.slice(8).map((a) => (
                <li key={a.slug}>
                  <Link to={`/appliance-repair-${a.slug}-nj`} className="text-white/60 hover:text-orange-400 transition-colors">
                    {a.name}, NJ
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8 text-center text-sm text-white/50">
          © {new Date().getFullYear()} Major Appliance Repair NJ. All Rights Reserved. Licensed and insured in New Jersey.
        </div>
      </div>
    </footer>
  );
};


// Mobile CTA Bar - Book Online primary
const MobileCTABar = () => {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-orange-100 p-3 md:hidden z-40 shadow-2xl">
      <div className="flex gap-3">
        <a 
          href="/#services"
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-center py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25"
          data-testid="mobile-book-btn"
        >
          Book Online
          <span className="bg-white/25 text-[10px] font-bold px-1.5 py-0.5 rounded-full">-5%</span>
        </a>
        <a 
          href={PHONE_URL}
          onClick={handlePhoneClick}
          className="flex-1 bg-navy text-white text-center py-3.5 rounded-full font-semibold flex items-center justify-center gap-2"
          data-testid="mobile-call-btn"
        >
          <Phone className="w-5 h-5" strokeWidth={2.5} /> Call
        </a>
      </div>
    </div>
  );
};

// Zenbooker Widget
const ZenbookerWidget = ({ url }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <iframe 
        src={url}
        title="Book Service"
        style={{ width: '100%', minWidth: '320px', height: '700px', border: 0 }}
        allow="payment"
      />
    </div>
  );
};

// Booking Page
const BookingPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find(s => s.id === serviceId);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {'send_to': 'AW-17526247982/aWUjCIPpm-0bEK6klaVB'});
    }
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Service not found</h1>
          <Link to="/" className="text-orange-500 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Header minimal />
      
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-navy/60 hover:text-orange-500 mb-6 text-sm font-semibold"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Services
          </button>

          <div className="text-center mb-10">
            <span className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3 inline-block">Book Online</span>
            <h1 className="font-display text-3xl md:text-5xl text-navy leading-tight mb-3">
              {service.name} <span className="text-orange-500">Repair</span>
            </h1>
            <p className="text-navy/60 text-lg">Pick a date and time — we'll take care of the rest.</p>
          </div>

          <div className="bg-white rounded-3xl border border-orange-100 shadow-lg overflow-hidden">
            <ZenbookerWidget url={service.zenbookerUrl} />
          </div>

          <div className="mt-8 text-center">
            <p className="text-navy/60 mb-3">Prefer to call?</p>
            <a 
              href={PHONE_URL}
              onClick={handlePhoneClick}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg shadow-orange-500/25"
              data-testid="booking-call-btn"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              {BUSINESS.phoneFormatted}
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Home Page
const HomePage = () => {
  const homeSchema = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://majorappliancerepair.us/#business",
      name: "Major Appliance Repair NJ",
      image:
        "https://customer-assets.emergentagent.com/job_quick-repair-23/artifacts/ygttzoc1_major_appliance_repair_logo.png",
      url: "https://majorappliancerepair.us/",
      telephone: "+1-609-451-2225",
      email: "Hello@bbmappliances.com",
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress: "1099 White Horse Rd 2nd Floor",
        addressLocality: "Voorhees Township",
        addressRegion: "NJ",
        postalCode: "08043",
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: 39.8436, longitude: -74.9725 },
      areaServed: [
        "Gloucester County NJ",
        "Camden County NJ",
        "Burlington County NJ",
        "Voorhees NJ",
        "Cherry Hill NJ",
        "Sicklerville NJ",
        "Mount Laurel NJ",
        "Marlton NJ",
        "Sewell NJ",
        "Washington Township NJ",
        "Wenonah NJ",
        "Woodbury NJ",
      ],
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ],
          opens: "08:00",
          closes: "18:00",
        },
      ],
      sameAs: ["https://www.facebook.com/profile.php?id=61583085047292"],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "159",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Major Appliance Repair NJ | Same-Day Service in South Jersey"
        description="Same-day appliance repair in South Jersey. Refrigerators, washers, dryers, ovens, dishwashers. 4.9★ rated, licensed, 30-day warranty. Save 5% online."
        path="/"
        schema={homeSchema}
      />
      <Header />
      <main>
        <Hero />
        <ServiceFeeBanner />
        <Services />
        <HowItWorks />
        <BrandsSection />
        <WhyUs />
        <Reviews />
        <ServiceAreas />
        <FAQ />
        <Contact />
      </main>
      <Footer />
      <MobileCTABar />
    </>
  );
};

// Main App
function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/book/:serviceId" element={<BookingPage />} />

            {/* SEO Service Pages */}
            {SERVICE_PAGES.map((s) => (
              <Route
                key={s.slug}
                path={`/${s.slug}`}
                element={
                  <ServicePage
                    HeaderComponent={Header}
                    FooterComponent={Footer}
                    MobileBarComponent={MobileCTABar}
                  />
                }
              />
            ))}

            {/* SEO Area Pages (counties + cities) */}
            {ALL_AREAS.map((a) => (
              <Route
                key={a.slug}
                path={`/appliance-repair-${a.slug}-nj`}
                element={
                  <AreaPage
                    HeaderComponent={Header}
                    FooterComponent={Footer}
                    MobileBarComponent={MobileCTABar}
                  />
                }
              />
            ))}

            {/* Redirects */}
            <Route path="/refrigerator-booking" element={<Navigate to="/book/refrigerator" replace />} />
            <Route path="/refrigerator-booking/" element={<Navigate to="/book/refrigerator" replace />} />
            <Route path="/washer-booking" element={<Navigate to="/book/washer" replace />} />
            <Route path="/washer-booking/" element={<Navigate to="/book/washer" replace />} />
            <Route path="/dryer-booking" element={<Navigate to="/book/dryer" replace />} />
            <Route path="/dryer-booking/" element={<Navigate to="/book/dryer" replace />} />
            <Route path="/dishwsher-booking" element={<Navigate to="/book/dishwasher" replace />} />
            <Route path="/dishwsher-booking/" element={<Navigate to="/book/dishwasher" replace />} />
            <Route path="/dishwasher-booking" element={<Navigate to="/book/dishwasher" replace />} />
            <Route path="/dishwasher-booking/" element={<Navigate to="/book/dishwasher" replace />} />
            <Route path="/garbage-disposal-booking" element={<Navigate to="/book/garbage-disposal" replace />} />
            <Route path="/garbage-disposal-booking/" element={<Navigate to="/book/garbage-disposal" replace />} />
            <Route path="/book-now" element={<Navigate to="/book/oven" replace />} />
            <Route path="/book-now/" element={<Navigate to="/book/oven" replace />} />
            <Route path="/service-page" element={<Navigate to="/" replace />} />
            <Route path="/service-page/" element={<Navigate to="/" replace />} />
            <Route path="/contac-us-final" element={<Navigate to="/" replace />} />
            <Route path="/contac-us-final/" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
